import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { User, Menu, Home, Settings, HelpCircle, ExternalLink, Globe, CircleDollarSign, Link, Zap } from 'lucide-react';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';

export const LinksPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentLanguage, switchLanguage } = useLanguage();

  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;

  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');


  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    { id: 'eng', label: 'English', onClick: () => switchLanguage('ENG') },
    { id: 'rus', label: 'Русский', onClick: () => switchLanguage('RUS') }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  // Получаем массив блоков из ресурсов i18n (безопасно)
  const blocks = i18n.getResource(i18n.language, 'translation', 'links_blocks') as any[];

  return (
    <div className="min-h-screen bg-background" 
         style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + ${safeAreaTop})` : '5rem' }}>
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
              <Dropdown id="account" trigger={<User className="w-5 h-5 text-iconPrimary" />} items={accountItems} align="right" />
              <Dropdown id="language" trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>} items={languageItems} align="right" />
              <Dropdown id="pages" trigger={<Menu className="w-5 h-5 text-iconPrimary" />} items={pagesItems} align="right" />
          </div>
        </div>
      </header>
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
        <h2 className="flex items-center justify-center gap-2 text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 text-center font-sans">
          <Link className="w-7 h-7 text-primary" />
          <span>{t('links_title')}</span>
        </h2>
        <div className="flex flex-col gap-6">
          {Array.isArray(blocks) && blocks.map((block, idx) => {
            const lines = typeof block.desc === 'string' ? block.desc.split('\n') : [];
            const bulletLines: string[] = [];
            const otherLines: string[] = [];
            let zapLine: string | null = null;

            for (const line of lines) {
              if (!line || line.trim().length === 0) continue;
              if (line.trim().startsWith('- ')) {
                bulletLines.push(line.trim().slice(2));
              } else if (line.trim().startsWith('⚡️')) {
                zapLine = line.trim().replace(/^⚡️\s*/, '');
              } else {
                otherLines.push(line);
              }
            }

            return (
              <div key={idx} className="bg-header border border-border rounded-lg p-6 flex flex-col items-center text-center">
                <div className="text-xl md:text-2xl font-bold text-text mb-1">{block.title}</div>
                <div className="w-full flex flex-col items-center text-textSubtle">
                  {otherLines.map((ln: string, i: number) => (
                    <p key={i} className="text-base md:text-lg mb-1 whitespace-pre-line">{ln}</p>
                  ))}
                  {bulletLines.length > 0 && (
                    <ul className="text-left list-disc pl-5 md:pl-6 my-2">
                      {bulletLines.map((ln, i) => (
                        <li key={i} className="text-base md:text-lg leading-snug mb-1">{ln}</li>
                      ))}
                    </ul>
                  )}
                  {zapLine && (
                    <div className="flex items-start gap-2 text-base md:text-lg mt-1">
                      <Zap className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>{zapLine}</span>
                    </div>
                  )}
                </div>
                <a
                  href={block.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-primary font-bold hover:underline text-base md:text-lg transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0"
                >
                  {block.button}
                </a>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}; 